/**
 * useWidgetData hook — Sprint 2 F1.
 *
 * Fetches widget data by widget_id with filters. Handles loading/error states.
 *
 * Sprint 4 hardening:
 *   - `stableFilterKey` dedupes filter changes that don't actually move the needle
 *     (sorted arrays, dropped empties) so empty array vs missing key produce the
 *     same fetch.
 *   - 250 ms debounce coalesces rapid filter mutations (date scrub, multi-select).
 *   - AbortController cancels inflight requests on filter change so stale
 *     responses can't render after newer ones.
 *   - When `paused` is true (filters not yet hydrated), we render the skeleton
 *     state without firing a request.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { v3Api } from '../hooks/useApi';
import { stableFilterKey } from './filters/filter-key';

const DEBOUNCE_MS = 250;

export function useWidgetData(widgetId, filters, opts = {}) {
    const { paused = false } = opts;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const filterKey = useMemo(() => stableFilterKey(filters), [filters]);

    // Hold the latest filters in a ref so the timed fetch reads the freshest
    // value even if a new debounce cycle starts mid-flight.
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    // Refetch counter — bumped by `refetch()` to bypass debounce.
    const [refetchTick, setRefetchTick] = useState(0);
    const prevTickRef = useRef(0);

    useEffect(() => {
        if (!widgetId) return;
        if (paused) return;

        const ac = new AbortController();
        // Discrete user actions (preset clicks, refetch button) skip the debounce
        // by using `refetchTick` in the dep array.
        const isExplicit = refetchTick > 0 && refetchTick !== prevTickRef.current;
        prevTickRef.current = refetchTick;
        const delay = isExplicit ? 0 : DEBOUNCE_MS;

        const t = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await v3Api.widgets.data(
                    widgetId,
                    filtersRef.current || {},
                    { signal: ac.signal }
                );
                if (!ac.signal.aborted) setData(res);
            } catch (err) {
                if (ac.signal.aborted) return;
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                setError(err.response?.data?.error || err.message);
            } finally {
                if (!ac.signal.aborted) setLoading(false);
            }
        }, delay);

        return () => {
            clearTimeout(t);
            ac.abort();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgetId, filterKey, paused, refetchTick]);

    return {
        data,
        loading,
        error,
        refetch: () => setRefetchTick(t => t + 1),
    };
}
